<template>
    <div class="shipment-items-list">
        <div class="shipment-items-list__header">
            <div class="shipment-items-list__header-actions">
                <v-btn
                    v-if="selectedItems.length > 0"
                    color="primary"
                    small
                    :loading="isClosing"
                    @click="closeSelectedItems"
                >
                    {{ $t('pages.shipments.closeSelected') }}
                </v-btn>
                <v-btn
                    v-if="hasReturnableSelectedItems"
                    color="warning"
                    small
                    :loading="isReturning"
                    @click="returnSelectedItems"
                >
                    {{ $t('pages.shipments.returnSelected') }}
                </v-btn>
            </div>
        </div>

        <ShipmentsFilters
            :use-shipment-items-filters="true"
            @filters-change="updateFilters"
            @reset-filters="resetFilters"
        />

        <div class="shipment-items-list__table-wrapper">
            <v-data-table
                :value="selectedItems"
                :headers="itemHeaders"
                :items="itemsWithKeys"
                :items-per-page="-1"
                :loading="isFetching"
                :no-data-text="$t('pages.shipments.noDataAvailable')"
                :item-disabled="isItemDisabled"
                dense
                hide-default-footer
                show-select
                item-key="uniqueKey"
                class="shipment-items-list__table"
                @input="onSelectionChange"
            >
                <!--    eslint-disable-next-line -->
                <template #item.wig="{ item }">
                    {{ item.wig?.barcode || item.wig_id || '-' }}
                </template>
                <!--    eslint-disable-next-line -->
                <template #item.client="{ item }">
                    <span class="nowrap-cell-tablet">{{ formatClientWithStylist(item) }}</span>
                </template>
                <!--    eslint-disable-next-line -->
                <template #item.price="{ item }">
                    <span class="nowrap-cell">{{ formatPrice(item.price || 0, item.wig.price_currency) }}</span>
                </template>
                <!--    eslint-disable-next-line -->
                <template #item.remaining="{ item }">
                    <span
                        class="nowrap-cell"
                        :class="{ 'shipment-items-list__debt--unpaid': (item.remaining || 0) > 0 }"
                    >{{
                        formatPrice(
                            item.remaining || 0,
                            item.wig.price_currency
                        )
                    }}</span>
                </template>
                <!--    eslint-disable-next-line -->
                <template #item.total_paid="{ item }">
                    <span class="nowrap-cell">{{
                        formatPrice(
                            item.total_paid || 0,
                            item.wig.price_currency
                        )
                    }}</span>
                </template>
                <!--    eslint-disable-next-line -->
                <template #item.document_number="{ item }">
                    {{ item.document_number || '-' }}
                </template>
                <!--    eslint-disable-next-line -->
                <template #item.document_link="{ item }">
                    <ShipmentDocumentLink
                        :link="item.document_link"
                        button-class="shipment-items-list__document-link"
                        icon-class="bi bi-file-earmark-pdf shipment-items-list__document-icon"
                    />
                </template>
                <!--    eslint-disable-next-line -->
                <template #item.closed_at="{ item }">
                    <span class="nowrap-cell">
                        <DateCell
                            v-if="item.closed_at"
                            :value="item.closed_at"
                            :utc="false"
                        />
                        <span v-if="!item.closed_at">-</span>
                    </span>
                </template>
                <!--    eslint-disable-next-line -->
                <template #item.receipts="{ item }">
                    <ShipmentReceiptsDisplay :receipts="item.receipts" />
                </template>
                <!--    eslint-disable-next-line -->
                <template #item.actions="{ item }">
                    <div class="shipment-items-list__actions-cell">
                        <v-btn
                            v-if="!item.closed_at && !isItemReturned(item)"
                            color="primary"
                            small
                            :loading="
                                isClosing &&
                                closingItemId === getItemId(item)
                            "
                            @click="closeItem(item)"
                        >
                            {{ $t('pages.shipments.close') }}
                        </v-btn>
                        <v-btn
                            v-if="canReturnItem(item)"
                            color="warning"
                            small
                            :loading="
                                isReturning &&
                                returningItemId === getItemId(item)
                            "
                            @click="returnItem(item)"
                        >
                            {{ $t('pages.shipments.return') }}
                        </v-btn>
                        <span
                            v-if="item.closed_at"
                            class="shipment-items-list__closed-badge"
                        >
                            {{ $t('pages.shipments.closed') }}
                        </span>
                        <span
                            v-if="isItemReturned(item)"
                            class="shipment-items-list__returned-badge"
                        >
                            {{ $t('pages.shipments.returned') }}
                        </span>
                    </div>
                </template>
            </v-data-table>
        </div>

        <v-pagination
            v-if="pagination && pagination.lastPage > 1"
            v-model="page"
            class="pagination"
            :total-visible="7"
            :length="pagination.lastPage"
            color="#3bafda"
            @input="onPageChange"
        ></v-pagination>
    </div>
</template>

<script>
import shipmentItemsMixin from '@/mixins/shipment-items.mixin'
import ShipmentsFilters from '@/components/shipments/shipments-filters'
import DateCell from '@/components/common/cells/date-cell.vue'
import ShipmentDocumentLink from '@/components/shipments/shipment-document-link.vue'
import ShipmentReceiptsDisplay from '@/components/shipments/shipment-receipts-display.vue'
import { mapActions, mapGetters } from 'vuex'
import { actionTypes, getterTypes } from '@/store/modules/shipments'
import { actionTypes as modalNotificationActionTypes } from '@/store/modules/modal-notifications'
import { actionTypes as confirmerActionTypes } from '@/store/modules/confirmer'
import { actionTypes as modalsActionTypes } from '@/store/modules/modals'
import shipmentsApi from '@/api/shipments'
import popUpTypes from '@/types/pop-up-types'
import {
    canReturnItem as canReturnItemUtil,
    filterReturnableItems,
    getItemId as getItemIdUtil,
    isItemReturned as isItemReturnedUtil,
    createBulkReturnPayload,
    handleReturnError,
    resetSelection,
    handleBulkOperationResponse,
} from '@/helpers/shipment-items.utils'

export default {
    name: 'ShipmentItemsList',
    components: {
        ShipmentsFilters,
        DateCell,
        ShipmentDocumentLink,
        ShipmentReceiptsDisplay,
    },
    mixins: [shipmentItemsMixin],
    data() {
        return {
            selectedItems: [],
            isClosing: false,
            closingItemId: null,
            returningItemId: null,
            page: 1,
            hasInitialized: false,
            localFilters: {
                status: '',
                search: '',
                dateFrom: '',
                dateTo: '',
                usmanager_id: null,
            },
        }
    },
    computed: {
        ...mapGetters('shipments', {
            shipmentItems: getterTypes.getShipmentItems,
            isFetching: getterTypes.isFetchingShipmentItems,
            filters: getterTypes.getShipmentItemsFilters,
            paginationStore: getterTypes.getShipmentItemsPagination,
            isReturning: getterTypes.isReturning,
        }),
        itemHeaders() {
            return this.getItemHeaders().map((header) =>
                header.value === 'client'
                    ? {
                          ...header,
                          text: this.$t('pages.shipments.clientWithStylist'),
                      }
                    : header
            )
        },
        itemsWithKeys() {
            const items = this.shipmentItems?.items || []
            return items.map((item, index) => ({
                ...item,
                uniqueKey: this.getItemId(item) || `item-${index}`,
            }))
        },
        pagination() {
            return this.shipmentItems?.pagination ?? null
        },
        hasReturnableSelectedItems() {
            return (
                this.selectedItems?.length > 0 &&
                this.selectedItems.every((item) => canReturnItemUtil(item))
            )
        },
    },
    watch: {
        paginationStore: {
            handler(newPagination) {
                if (newPagination && newPagination.currentPage) {
                    this.page = newPagination.currentPage
                }
            },
            deep: true,
        },
    },
    mounted() {
        this.page = this.paginationStore?.currentPage || 1

        const storeFilters = this.filters || {}
        const dateFrom = storeFilters.dateFrom || storeFilters.date_from || ''
        const dateTo = storeFilters.dateTo || storeFilters.date_to || ''

        if (!dateFrom && !dateTo) {
            const today = this.getTodayDate()
            this.localFilters = {
                status: storeFilters.status || '',
                search: storeFilters.search || '',
                dateFrom: '2026-01-01',
                dateTo: today,
                usmanager_id: storeFilters.usmanager_id
                    ? Number(storeFilters.usmanager_id)
                    : null,
            }
        }

        if (dateFrom || dateTo) {
            this.localFilters = {
                status: storeFilters.status || '',
                search: storeFilters.search || '',
                dateFrom,
                dateTo,
                usmanager_id: storeFilters.usmanager_id
                    ? Number(storeFilters.usmanager_id)
                    : null,
            }
        }
    },
    methods: {
        ...mapActions('shipments', {
            setFiltersAction: actionTypes.setShipmentItemsFilters,
            setPaginationAction: actionTypes.setShipmentItemsPagination,
            fetchShipmentItemsAction: actionTypes.fetchShipmentItems,
            bulkReturnShipmentItems: actionTypes.bulkReturnShipmentItems,
        }),
        ...mapActions('modalNotifications', {
            showError: modalNotificationActionTypes.error,
            showSuccess: modalNotificationActionTypes.success,
        }),
        ...mapActions('confirmer', {
            confirmHandle: confirmerActionTypes.confirmHandle,
        }),
        ...mapActions('modals', {
            openModal: modalsActionTypes.openPopUp,
        }),
        updateFilters(filters) {
            this.localFilters = { ...filters }
            this.page = 1

            if (filters.dateFrom && filters.dateTo) {
                this.fetchShipmentItems()
                return
            }

            if (!this.hasInitialized) {
                this.hasInitialized = true
                return
            }

            this.fetchShipmentItems()
        },
        resetFilters() {
            const today = this.getTodayDate()
            this.localFilters = {
                status: 'open',
                search: '',
                dateFrom: '2026-01-01',
                dateTo: today,
                usmanager_id: null,
            }
            this.page = 1
            this.fetchShipmentItems()
        },
        getTodayDate() {
            const date = new Date()
            const year = date.getFullYear()
            const month = date.getMonth() + 1
            const day = date.getDate()
            const addZero = (data) => (data < 10 ? '0' + data : data)
            return `${year}-${addZero(month)}-${addZero(day)}`
        },
        async onPageChange(page) {
            this.page = page
            this.setPaginationAction({ currentPage: page })
            await this.fetchShipmentItemsAction()
        },
        async fetchShipmentItems() {
            try {
                const { dateFrom, dateTo, status, usmanager_id, ...otherFilters } =
                    this.localFilters

                const filters = {
                    ...otherFilters,
                    status: status || '',
                }

                if (dateFrom && dateTo) {
                    filters.date_from = dateFrom
                    filters.date_to = dateTo
                }

                if (usmanager_id) {
                    filters.usmanager_id = Number(usmanager_id)
                }

                if (!usmanager_id) {
                    filters.usmanager_id = null
                }

                this.setFiltersAction(filters)
                this.setPaginationAction({ currentPage: this.page })
                await this.fetchShipmentItemsAction()
            } catch (error) {
                console.error('Error fetching shipment items:', error)
            }
        },
        closeItem(item) {
            const itemId = this.getItemId(item)
            if (!itemId || item.closed_at) return

            this.closingItemId = itemId
            this.openPaymentsModal({
                items: [item],
                onConfirm: this.handleCloseWithPayments,
            })
        },
        closeSelectedItems() {
            if (this.selectedItems.length === 0) return

            const itemsToClose = this.selectedItems.filter(
                (item) => !item.closed_at
            )

            if (itemsToClose.length === 0) {
                this.showError(this.$t('pages.shipments.allItemsAlreadyClosed'))
                return
            }

            this.openPaymentsModal({
                items: itemsToClose,
                onConfirm: this.handleCloseWithPayments,
            })
        },
        openPaymentsModal(payload) {
            this.openModal({
                name: popUpTypes.shipmentClosePayments,
                payload: {
                    ...payload,
                    onConfirm: (paymentsData) => {
                        payload.onConfirm(paymentsData)
                    },
                },
            })
        },
        async handleCloseWithPayments(paymentsData) {
            this.isClosing = true

            try {
                const payload = {
                    items: paymentsData.items,
                }
                const response = await shipmentsApi.bulkPaymentShipmentItems(payload)
                await this.fetchShipmentItems()
                this.selectedItems = []
                
                handleBulkOperationResponse(response, {
                    showSuccess: (messageKey, params) => {
                        this.showSuccess(this.$t(messageKey, params))
                    },
                    openModal: this.openModal,
                }, 'close')
            } catch (error) {
                const errorMessage =
                    error?.response?.data?.message ||
                    this.$t('pages.shipments.closeError')
                this.showError(errorMessage)
            } finally {
                this.isClosing = false
                this.closingItemId = null
            }
        },
        isItemReturned(item) {
            return isItemReturnedUtil(item)
        },
        canReturnItem(item) {
            return canReturnItemUtil(item)
        },
        getItemId(item) {
            return getItemIdUtil(item)
        },
        async returnItem(item) {
            const itemId = this.getItemId(item)
            if (!itemId || !canReturnItemUtil(item)) return

            try {
                await this.confirmHandle(
                    this.$t('pages.shipments.confirmReturnItem')
                )
                this.returningItemId = itemId

                const payload = createBulkReturnPayload([item])
                const response = await this.bulkReturnShipmentItems(payload)

                resetSelection(this)
                
                handleBulkOperationResponse(response, {
                    showSuccess: (messageKey, params) => {
                        this.showSuccess(this.$t(messageKey, params))
                    },
                    openModal: this.openModal,
                }, 'return')
            } catch (error) {
                handleReturnError(error, (message) => {
                    const translatedMessage = message?.startsWith('pages.')
                        ? this.$t(message)
                        : message
                    this.showError(translatedMessage)
                }, 'pages.shipments.returnError')
            } finally {
                this.returningItemId = null
            }
        },
        async returnSelectedItems() {
            if (this.selectedItems.length === 0) return

            const itemsToReturn = filterReturnableItems(this.selectedItems)
            if (itemsToReturn.length === 0) {
                this.showError(
                    this.$t('pages.shipments.allItemsAlreadyReturned')
                )
                return
            }

            try {
                await this.confirmHandle(
                    this.$t('pages.shipments.confirmReturnItems', {
                        count: itemsToReturn.length,
                    })
                )

                const payload = createBulkReturnPayload(itemsToReturn)
                const response = await this.bulkReturnShipmentItems(payload)

                resetSelection(this)
                
                handleBulkOperationResponse(response, {
                    showSuccess: (messageKey, params) => {
                        this.showSuccess(this.$t(messageKey, params))
                    },
                    openModal: this.openModal,
                }, 'return')
            } catch (error) {
                handleReturnError(error, (message) => {
                    const translatedMessage = message?.startsWith('pages.')
                        ? this.$t(message)
                        : message
                    this.showError(translatedMessage)
                }, 'pages.shipments.returnError')
            } finally {
                this.returningItemId = null
            }
        },
        onSelectionChange(selection) {
            const selectionArray = selection || []
            const filtered = selectionArray.filter((item) => {
                if (!item) return false
                return !item.closed_at && !isItemReturnedUtil(item)
            })

            const filteredKeys = filtered
                .map((i) => i.uniqueKey)
                .sort()
                .join(',')
            const currentKeys = this.selectedItems
                .map((i) => i.uniqueKey)
                .sort()
                .join(',')

            if (
                filtered.length !== selectionArray.length ||
                filteredKeys !== currentKeys
            ) {
                this.$nextTick(() => {
                    this.selectedItems = filtered
                })
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.shipment-items-list {
    width: 100%;

    &__debt--unpaid {
        color: #d32f2f;
    }

    &__header {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        margin-bottom: 16px;
        flex-wrap: wrap;
        gap: 12px;
    }

    &__title {
        font-size: 18px;
        font-weight: 600;
        margin: 0;
    }

    &__header-actions {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
    }

    &__table-wrapper {
        width: 100%;
        margin-top: 16px;
    }

    &__table {
        width: 100%;
    }

    &__closed-badge {
        color: #4caf50;
        font-size: 12px;
        font-weight: 600;
    }

    &__returned-badge {
        color: #ff9800;
        font-size: 12px;
        font-weight: 600;
    }

    &__actions-cell {
        display: flex;
        gap: 8px;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    &__document-link {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-decoration: none;
        transition: opacity 0.2s;
        padding: 2px;
        border-radius: 4px;
        max-height: 24px;
        max-width: 24px;

        &:hover {
            opacity: 0.7;
        }

        svg {
            display: block;
        }
    }

    &__document-icon {
        width: 18px;
        height: 18px;
        color: red;
    }
}

.pagination {
    padding: 8px 0 0 0 !important;
}
</style>
